import React, { useState } from "react";
import { Heart, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * TipDjButton — listener-side "Tap to tip the DJ" pill that lives next to
 * the ON AIR badge on RadioStation. Clicking it opens a quick-pick modal
 * with four tip tiers ($1 / $5 / $10 / $25) + an optional message. Uses
 * the existing `/api/tips` endpoint which routes through Stripe checkout.
 *
 * Hidden when there's no live host (listeners can only tip while a DJ is
 * actually mixing). Anonymous users get redirected to /login first so the
 * tip lands on the correct account.
 */
export default function TipDjButton({ hostId, hostName }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(5);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!hostId) return null;

  // Anonymous users get bounced to /login the moment they tap the pill —
  // matches the listener-flow spec (no flash of the modal). Self-tip block
  // is also enforced here so the host doesn't see their own modal at all.
  const handleOpen = () => {
    if (!user) {
      toast.info("Sign in to tip the DJ");
      return nav("/login");
    }
    if (user.user_id === hostId) {
      toast.info("You can't tip yourself");
      return;
    }
    setOpen(true);
  };

  const sendTip = async () => {
    if (!user) {
      toast.info("Sign in to tip the DJ");
      return nav("/login");
    }
    if (user.user_id === hostId) {
      toast.info("You can't tip yourself");
      return;
    }
    if (!(amount > 0)) {
      toast.error("Pick an amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tips", {
        target_id: hostId,
        amount,
        message: message.slice(0, 200),
        origin_url: window.location.origin,
      });
      // Open Stripe checkout in a NEW tab so the listener's stream keeps
      // playing back here. NB: we deliberately do NOT pass "noopener" —
      // Chromium returns null from window.open when "noopener" is set,
      // which would falsely trigger the popup-blocked fallback below and
      // navigate this tab to Stripe too. The destination is a known Stripe
      // checkout URL so reverse-tabnabbing is not a concern.
      const w = window.open(data.url, "_blank");
      if (!w) window.location.href = data.url;
      setBusy(false);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="radio-station-tip-dj"
        onClick={handleOpen}
        title={`Tip ${hostName || "the DJ"}`}
        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-100 bg-emerald-600/80 hover:bg-emerald-500 border border-emerald-300/40 rounded-full px-2 py-0.5 transition"
      >
        <Heart className="w-3 h-3" />
        Tip the DJ
      </button>

      {open && (
        <div
          data-testid="radio-station-tip-modal"
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="bg-[#0A0D14] border border-emerald-500/40 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-emerald-300 uppercase tracking-[0.25em] text-[10px] font-black">Tap In Tips</div>
                <h3 className="text-xl font-black text-white">Tip {hostName || "the DJ"}</h3>
                <p className="text-white/60 text-xs mt-1">Throw points at the host for the live mix.</p>
              </div>
              <button
                onClick={() => !busy && setOpen(false)}
                aria-label="Close"
                className="text-white/50 hover:text-white"
                disabled={busy}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-4">
              {[1, 5, 10, 25].map((amt) => (
                <button
                  key={amt}
                  data-testid={`radio-station-tip-amount-${amt}`}
                  onClick={() => setAmount(amt)}
                  disabled={busy}
                  className={`py-2.5 rounded font-black text-base border transition ${
                    amount === amt
                      ? "bg-emerald-500/30 text-emerald-100 border-emerald-400/60"
                      : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>

            <input
              data-testid="radio-station-tip-message"
              type="text"
              maxLength={200}
              placeholder="Add a message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={busy}
              className="w-full bg-black/50 border border-white/10 focus:border-emerald-400/60 outline-none rounded px-3 py-2 text-sm text-white placeholder-white/30 mb-4"
            />

            <button
              data-testid="radio-station-tip-confirm"
              onClick={sendTip}
              disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-black py-2.5 rounded-full"
            >
              {busy ? "Redirecting…" : `Send $${amount} tip`}
            </button>
            <p className="text-white/40 text-[10px] text-center mt-2">
              Secure checkout via Stripe. Most of every tip goes straight to the host.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
